import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import profileManager from '../services/profileManager';

const STORAGE_KEY = 'mathlete-player-state-v2';
const OLD_STORAGE_KEY = 'mathlete-player-state-v1';

const charactersCatalog = [
  // STARTER
  {
    id: 'astro-otter',
    name: 'Astro Otter',
    price: 0,
    description: 'A curious explorer who splashes through number nebulae.',
    emoji: '🦦',
    rarity: 'starter',
  },
  // COMMON
  {
    id: 'puzzle-panda',
    name: 'Puzzle Panda',
    price: 100,
    description: 'Loves solving fact ladders while munching bamboo snacks.',
    emoji: '🐼',
    rarity: 'common',
  },
  {
    id: 'cosmic-frog',
    name: 'Cosmic Frog',
    price: 150,
    description: 'Hops between star systems, calculating trajectories with ease.',
    emoji: '🐸',
    rarity: 'common',
  },
  {
    id: 'math-lion',
    name: 'Math Lion',
    price: 200,
    description: 'Roars out correct answers with fierce confidence.',
    emoji: '🦁',
    rarity: 'common',
  },
  // UNCOMMON
  {
    id: 'rocket-raptor',
    name: 'Rocket Raptor',
    price: 300,
    description: 'Blasts off the launchpad every time you ace a mission.',
    emoji: '🦖',
    rarity: 'uncommon',
  },
  {
    id: 'cyber-fox',
    name: 'Cyber Fox',
    price: 400,
    description: 'Codes secret math hacks with neon-tipped tails and sparks.',
    emoji: '🦊',
    rarity: 'uncommon',
  },
  {
    id: 'binary-koala',
    name: 'Binary Koala',
    price: 500,
    description: 'Climbs computation trees while crunching complex numbers.',
    emoji: '🐨',
    rarity: 'uncommon',
  },
  // RARE
  {
    id: 'galaxy-sloth',
    name: 'Galaxy Sloth',
    price: 700,
    description: 'Floats calmly through the cosmos, collecting glittering coins.',
    emoji: '🦥',
    rarity: 'rare',
  },
  {
    id: 'wisdom-owl',
    name: 'Wisdom Owl',
    price: 900,
    description: 'Sees patterns in the dark and solves ancient math riddles.',
    emoji: '🦉',
    rarity: 'rare',
  },
  {
    id: 'quantum-wolf',
    name: 'Quantum Wolf',
    price: 1100,
    description: 'Hunts down solutions across parallel calculation dimensions.',
    emoji: '🐺',
    rarity: 'rare',
  },
  // EPIC
  {
    id: 'nebula-unicorn',
    name: 'Nebula Unicorn',
    price: 1400,
    description: 'Magical horn glows brighter with each perfect score.',
    emoji: '🦄',
    rarity: 'epic',
  },
  {
    id: 'thunder-eagle',
    name: 'Thunder Eagle',
    price: 1700,
    description: 'Soars through storm clouds, striking answers with lightning speed.',
    emoji: '🦅',
    rarity: 'epic',
  },
  {
    id: 'starwhale',
    name: 'Starwhale',
    price: 2000,
    description: 'Swims through asteroid belts, singing songs of multiplication.',
    emoji: '🐋',
    rarity: 'epic',
  },
  // LEGENDARY
  {
    id: 'azure-dragon',
    name: 'Azure Dragon',
    price: 2500,
    description: 'Commands the winds of wisdom with sapphire scales.',
    emoji: '🐉',
    rarity: 'legendary',
  },
  {
    id: 'ruby-dragon',
    name: 'Ruby Dragon',
    price: 3500,
    description: 'Breathes fire that transforms confusion into clarity.',
    emoji: '🐲',
    rarity: 'legendary',
  },
  {
    id: 'cosmic-dragon-king',
    name: 'Cosmic Dragon King',
    price: 5000,
    description: 'Supreme ruler of all mathematical realms. The ultimate achievement!',
    emoji: '🔥',
    rarity: 'legendary',
  },
];

const defaultState = {
  coins: 0,
  ownedCharacters: [charactersCatalog[0].id],
  equippedCharacter: charactersCatalog[0].id,
  difficultyStats: {
    multiplication: {
      easy: { attempts: 0, correct: 0 },
      medium: { attempts: 0, correct: 0 },
      hard: { attempts: 0, correct: 0 },
    },
    fractions: {
      easy: { attempts: 0, correct: 0 },
      medium: { attempts: 0, correct: 0 },
      hard: { attempts: 0, correct: 0 },
    },
  },
  achievements: {
    multiplication: {
      mediumReady: false,
      hardReady: false,
    },
    fractions: {
      mediumReady: false,
      hardReady: false,
    },
  },
};

const GameContext = createContext(undefined);

const loadState = () => {
  if (typeof window === 'undefined') {
    return defaultState;
  }

  try {
    // Check for old v1 data first - MIGRATION LOGIC
    const oldStored = window.localStorage.getItem(OLD_STORAGE_KEY);
    if (oldStored) {
      console.log('🔄 Migrating from v1 to v2: Resetting characters due to price updates');
      try {
        const oldParsed = JSON.parse(oldStored);

        // Migrate old stats structure to new topic-based structure
        let migratedStats = defaultState.difficultyStats;
        if (oldParsed?.difficultyStats) {
          if (oldParsed.difficultyStats.easy && !oldParsed.difficultyStats.multiplication) {
            migratedStats = {
              multiplication: {
                easy: oldParsed.difficultyStats.easy || defaultState.difficultyStats.multiplication.easy,
                medium: oldParsed.difficultyStats.medium || defaultState.difficultyStats.multiplication.medium,
                hard: oldParsed.difficultyStats.hard || defaultState.difficultyStats.multiplication.hard,
              },
              fractions: defaultState.difficultyStats.fractions,
            };
          } else if (oldParsed.difficultyStats.multiplication) {
            migratedStats = {
              multiplication: oldParsed.difficultyStats.multiplication || defaultState.difficultyStats.multiplication,
              fractions: oldParsed.difficultyStats.fractions || defaultState.difficultyStats.fractions,
            };
          }
        }

        // Migrate old achievements structure
        let migratedAchievements = defaultState.achievements;
        if (oldParsed?.achievements) {
          if (oldParsed.achievements.mediumReady !== undefined && !oldParsed.achievements.multiplication) {
            migratedAchievements = {
              multiplication: {
                mediumReady: oldParsed.achievements.mediumReady || false,
                hardReady: oldParsed.achievements.hardReady || false,
              },
              fractions: defaultState.achievements.fractions,
            };
          } else if (oldParsed.achievements.multiplication) {
            migratedAchievements = {
              multiplication: oldParsed.achievements.multiplication || defaultState.achievements.multiplication,
              fractions: oldParsed.achievements.fractions || defaultState.achievements.fractions,
            };
          }
        }

        // Create migrated state: KEEP coins and progress, RESET characters
        const migratedState = {
          coins: Number.isFinite(oldParsed?.coins) ? oldParsed.coins : defaultState.coins,
          ownedCharacters: defaultState.ownedCharacters, // RESET to just free character
          equippedCharacter: defaultState.equippedCharacter, // RESET to free character
          difficultyStats: migratedStats, // KEEP learning progress
          achievements: migratedAchievements, // KEEP achievements
        };

        // Save migrated state to v2 and remove old v1 data
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(migratedState));
        window.localStorage.removeItem(OLD_STORAGE_KEY);

        console.log('✅ Migration complete: Characters reset, coins and progress preserved');
        return migratedState;
      } catch (migrationError) {
        console.error('Migration failed, using default state:', migrationError);
        window.localStorage.removeItem(OLD_STORAGE_KEY);
        return defaultState;
      }
    }

    // No old data, check for current v2 data
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return defaultState;
    }
    const parsed = JSON.parse(stored);

    // Migrate old stats structure to new topic-based structure
    let migratedStats = defaultState.difficultyStats;
    if (parsed?.difficultyStats) {
      // Check if it's the old structure (has 'easy' directly) or new structure (has 'multiplication')
      if (parsed.difficultyStats.easy && !parsed.difficultyStats.multiplication) {
        // Old structure - migrate to new
        migratedStats = {
          multiplication: {
            easy: parsed.difficultyStats.easy || defaultState.difficultyStats.multiplication.easy,
            medium: parsed.difficultyStats.medium || defaultState.difficultyStats.multiplication.medium,
            hard: parsed.difficultyStats.hard || defaultState.difficultyStats.multiplication.hard,
          },
          fractions: defaultState.difficultyStats.fractions,
        };
      } else if (parsed.difficultyStats.multiplication) {
        // New structure - ensure both topics exist
        migratedStats = {
          multiplication: parsed.difficultyStats.multiplication || defaultState.difficultyStats.multiplication,
          fractions: parsed.difficultyStats.fractions || defaultState.difficultyStats.fractions,
        };
      }
    }

    // Migrate old achievements structure to new topic-based structure
    let migratedAchievements = defaultState.achievements;
    if (parsed?.achievements) {
      // Check if it's the old structure (has 'mediumReady' directly) or new structure (has 'multiplication')
      if (parsed.achievements.mediumReady !== undefined && !parsed.achievements.multiplication) {
        // Old structure - migrate to new
        migratedAchievements = {
          multiplication: {
            mediumReady: parsed.achievements.mediumReady || false,
            hardReady: parsed.achievements.hardReady || false,
          },
          fractions: defaultState.achievements.fractions,
        };
      } else if (parsed.achievements.multiplication) {
        // New structure - ensure both topics exist
        migratedAchievements = {
          multiplication: parsed.achievements.multiplication || defaultState.achievements.multiplication,
          fractions: parsed.achievements.fractions || defaultState.achievements.fractions,
        };
      }
    }

    return {
      coins: Number.isFinite(parsed?.coins) ? parsed.coins : defaultState.coins,
      ownedCharacters: Array.isArray(parsed?.ownedCharacters) && parsed.ownedCharacters.length > 0
        ? parsed.ownedCharacters
        : defaultState.ownedCharacters,
      equippedCharacter: parsed?.equippedCharacter || defaultState.equippedCharacter,
      difficultyStats: migratedStats,
      achievements: migratedAchievements,
    };
  } catch (error) {
    console.warn('Failed to load game state from storage:', error);
    return defaultState;
  }
};

export const GameProvider = ({ children }) => {
  const initial = useMemo(() => loadState(), []);
  const [coins, setCoins] = useState(() => initial.coins);
  const [ownedCharacters, setOwnedCharacters] = useState(() => initial.ownedCharacters);
  const [equippedCharacter, setEquippedCharacter] = useState(() => initial.equippedCharacter);
  const [difficultyStats, setDifficultyStats] = useState(() => initial.difficultyStats);
  const [achievements, setAchievements] = useState(() => initial.achievements);
  const [recentAchievement, setRecentAchievement] = useState(null);

  // Initialize anonymous profile on mount
  useEffect(() => {
    const apiBaseUrl = process.env.REACT_APP_AI_API_BASE_URL || '';
    if (!apiBaseUrl) {
      console.warn('[GameContext] No API base URL configured, skipping profile initialization');
      return;
    }

    const initProfile = async () => {
      try {
        const result = await profileManager.initializeProfile(apiBaseUrl);
        console.log('[GameContext] Profile initialized:', result.apid);
      } catch (error) {
        console.warn('[GameContext] Profile initialization failed, continuing offline:', error);
      }
    };

    initProfile();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const payload = {
      coins,
      ownedCharacters,
      equippedCharacter,
      difficultyStats,
      achievements,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [coins, ownedCharacters, equippedCharacter, difficultyStats, achievements]);

  const addCoins = (amount) => {
    setCoins((prev) => Math.max(0, prev + amount));
  };

  const purchaseCharacter = (characterId) => {
    const character = charactersCatalog.find((entry) => entry.id === characterId);

    if (!character) {
      return { success: false, reason: 'not_found' };
    }
    if (ownedCharacters.includes(characterId)) {
      return { success: false, reason: 'already_owned' };
    }
    if (coins < character.price) {
      return { success: false, reason: 'insufficient_coins' };
    }

    setCoins((prev) => prev - character.price);
    setOwnedCharacters((prev) => [...prev, characterId]);
    setEquippedCharacter(characterId);

    return { success: true, character };
  };

  const equipCharacter = (characterId) => {
    if (!ownedCharacters.includes(characterId)) {
      return { success: false, reason: 'not_owned' };
    }
    setEquippedCharacter(characterId);
    return { success: true };
  };

  const updateDifficultyStats = (topic, difficulty, isCorrect) => {
    setDifficultyStats((prev) => {
      // Use clean template for new topics instead of copying live multiplication data
      const topicStats = prev[topic] || {
        easy: { attempts: 0, correct: 0 },
        medium: { attempts: 0, correct: 0 },
        hard: { attempts: 0, correct: 0 },
      };
      const updated = {
        ...prev,
        [topic]: {
          ...topicStats,
          [difficulty]: {
            attempts: topicStats[difficulty].attempts + 1,
            correct: topicStats[difficulty].correct + (isCorrect ? 1 : 0),
          },
        },
      };

      // Check for achievement unlocks for this specific topic
      const topicUpdated = updated[topic];
      const easyAccuracy = topicUpdated.easy.attempts >= 10
        ? (topicUpdated.easy.correct / topicUpdated.easy.attempts)
        : 0;
      const mediumAccuracy = topicUpdated.medium.attempts >= 10
        ? (topicUpdated.medium.correct / topicUpdated.medium.attempts)
        : 0;

      const topicAchievements = achievements[topic] || { mediumReady: false, hardReady: false };

      // Unlock Medium milestone if 70%+ accuracy on 10+ Easy attempts
      if (easyAccuracy >= 0.7 && !topicAchievements.mediumReady) {
        setAchievements((prev) => ({
          ...prev,
          [topic]: { ...(prev[topic] || { mediumReady: false, hardReady: false }), mediumReady: true },
        }));
        setRecentAchievement('mediumReady');
      }

      // Unlock Hard milestone if 70%+ accuracy on 10+ Medium attempts
      if (mediumAccuracy >= 0.7 && !topicAchievements.hardReady) {
        setAchievements((prev) => ({
          ...prev,
          [topic]: { ...(prev[topic] || { mediumReady: false, hardReady: false }), hardReady: true },
        }));
        setRecentAchievement('hardReady');
      }

      return updated;
    });
  };

  const clearRecentAchievement = () => {
    setRecentAchievement(null);
  };

  const resetProgress = () => {
    setCoins(defaultState.coins);
    setOwnedCharacters(defaultState.ownedCharacters);
    setEquippedCharacter(defaultState.equippedCharacter);
    setDifficultyStats(defaultState.difficultyStats);
    setAchievements(defaultState.achievements);
    setRecentAchievement(null);
  };

  const value = useMemo(
    () => ({
      coins,
      addCoins,
      charactersCatalog,
      ownedCharacters,
      purchaseCharacter,
      equipCharacter,
      equippedCharacter,
      resetProgress,
      difficultyStats,
      achievements,
      updateDifficultyStats,
      recentAchievement,
      clearRecentAchievement,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coins, ownedCharacters, equippedCharacter, difficultyStats, achievements, recentAchievement]
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const getCharacterById = (characterId) =>
  charactersCatalog.find((character) => character.id === characterId);
